<template>
  <transition name="drawer">
    <div v-if="open" class="budget-drawer-overlay" @click.self="$emit('close')">
      <div class="budget-drawer">

        <div class="budget-drawer-header">
          <h5 class="mb-0">
            <i class="fal fa-calculator me-2"></i>
            Calculate from Budget
          </h5>
          <button type="button" class="btn-close" @click="$emit('close')"></button>
        </div>

        <div class="budget-drawer-body">

          <p class="text-muted small mb-3">
            Enter a target total budget to reverse-engineer the required Base Effort Estimation hours, keeping all other parameters fixed.
          </p>

          <div class="mb-4">
            <label class="control-label">Target Total Budget</label>
            <div class="input-group">
              <span class="input-group-text">$</span>
              <input
                :value="targetBudget"
                type="text"
                inputmode="decimal"
                min="0"
                step="100"
                class="form-control form-control-lg"
                placeholder="e.g. 50000"
                autofocus
                @input="onBudgetInput"
              />
            </div>
          </div>

          <template v-if="breakdown">

            <table class="table table-sm">
              <tbody>
                <tr>
                  <td>Target Total Budget</td>
                  <td class="text-end text-muted">${{ breakdown.total_budget | currency }}</td>
                </tr>
                <tr>
                  <td>Less License (25%)</td>
                  <td class="text-end text-muted">-${{ breakdown.license | currency }}</td>
                </tr>
                <tr class="fw-bold">
                  <td>Dev Budget</td>
                  <td class="text-end">${{ breakdown.dev_budget | currency }}</td>
                </tr>
                <tr class="table-info fw-bold fs-6">
                  <td>Implied Base Effort Estimation</td>
                  <td class="text-end">{{ breakdown.base_effort_estimation.toFixed(2) }} hrs</td>
                </tr>
              </tbody>
            </table>

            <h6 class="text-uppercase text-muted small fw-bold mt-3 mb-2">Actual Budget at this Estimate</h6>
            <table class="table table-sm">
              <tbody>
                <tr>
                  <td>Base Sprints</td>
                  <td class="text-end text-muted">{{ breakdown.forward.base_sprints.toFixed(2) }} sprint(s)</td>
                </tr>
                <tr class="fw-bold">
                  <td>Sprints to Book</td>
                  <td class="text-end text-muted">{{ breakdown.forward.sprint_to_book.toFixed(2) }} sprint(s)</td>
                </tr>
                <tr>
                  <td>Total Billable Hours</td>
                  <td class="text-end text-muted">{{ breakdown.forward.total_billable_hours.toFixed(2) }} hrs</td>
                </tr>
                <tr>
                  <td>Dev Budget</td>
                  <td class="text-end text-muted">${{ breakdown.forward.dev_budget | currency }}</td>
                </tr>
                <tr>
                  <td>License</td>
                  <td class="text-end text-muted">${{ breakdown.forward.license | currency }}</td>
                </tr>
                <tr :class="budgetMatchClass" class="fw-bold fs-5">
                  <td>Total Budget</td>
                  <td class="text-end">${{ breakdown.forward.total_budget | currency }}</td>
                </tr>
              </tbody>
            </table>

          </template>

          <p v-else-if="targetBudget > 0" class="text-muted small">
            Cannot calculate - check that your hourly rate and other parameters are set.
          </p>

        </div>

        <div class="budget-drawer-footer">
          <button class="btn btn-secondary" @click="$emit('close')">
            Cancel
          </button>
          <button
            class="btn btn-primary"
            :disabled="!breakdown"
            @click="applyToForm"
          >
            <i class="fal fa-check me-1"></i>
            Apply {{ breakdown ? breakdown.base_effort_estimation.toFixed(2) + ' hrs' : '' }}
          </button>
        </div>

      </div>
    </div>
  </transition>
</template>

<script>
export default {
  props: {
    open: { type: Boolean, default: false },
    projectCalculation: { type: Object, required: true },
  },

  data() {
    return {
      targetBudget: null,
    }
  },

  watch: {
    open(isOpen) {
      if (isOpen) {
        this.targetBudget = null
      }
    },
  },

  computed: {
    breakdown() {
      if (!this.targetBudget || this.targetBudget <= 0) return null

      const { hourly_rate_cents, working_days, efficiency } = this.projectCalculation
      if (!hourly_rate_cents || !working_days || !efficiency) return null

      const dev_budget = this.targetBudget / 1.25
      const license = this.targetBudget - dev_budget

      // Binary search for the base_effort_estimation that produces the target budget.
      let low = 0
      let high = 4000
      let mid = 0

      for (let iteration = 0; iteration < 25; iteration++) {
        mid = (low + high) / 2
        if (high - low < 0.05) break
        if (this.forwardCalculate(mid).total_budget < this.targetBudget) {
          low = mid
        } else {
          high = mid
        }
      }

      const base_effort_estimation = Math.round(mid * 10) / 10
      const forward = this.forwardCalculate(base_effort_estimation)

      return {
        total_budget: this.targetBudget,
        license,
        dev_budget,
        base_effort_estimation,
        forward,
      }
    },

    budgetMatchClass() {
      if (!this.breakdown) return ''

      const diff = Math.abs(this.breakdown.forward.total_budget - this.breakdown.total_budget)

      return diff < 1 ? 'table-success' : 'table-warning'
    },
  },

  methods: {
    forwardCalculate(base_effort_estimation) {
      const { hourly_rate_cents, working_days, efficiency, dev_days, num_devs,
              warranty_percentage, sprint_rituals_estimate, scoping_sprint_testing,
              project_management, evaluation, contingency_days } = this.projectCalculation

      const hourly_rate = hourly_rate_cents / 100
      const base_effort = working_days * efficiency
      const base_sprints = Math.ceil(base_effort_estimation / base_effort / dev_days / 0.5) * 0.5
      const sprint_rituals = sprint_rituals_estimate * base_sprints * num_devs
      const effort = (base_effort_estimation + sprint_rituals) / base_effort
      const billable_dev_hours = effort * working_days
      const warranty = base_effort_estimation * warranty_percentage
      const sprints = (effort + contingency_days) / dev_days / num_devs
      const sprint_to_book = Math.ceil(sprints / 0.5) * 0.5
      const scoping_sprint_testing_total = sprint_to_book * scoping_sprint_testing
      const dev_bug_fixing = (10 - dev_days) * sprint_to_book * num_devs
      const project_management_total = project_management * sprint_to_book
      const total_billable_hours =
        billable_dev_hours +
        warranty +
        sprint_rituals +
        project_management_total +
        (
          (scoping_sprint_testing_total * num_devs) +
          (dev_bug_fixing * num_devs) +
          evaluation +
          contingency_days
        ) * working_days
      const dev_budget = total_billable_hours * hourly_rate
      const license = dev_budget * 0.25
      const total_budget = dev_budget + license

      return {
        base_effort, base_sprints, sprint_rituals, effort,
        billable_dev_hours, warranty, sprints, sprint_to_book,
        scoping_sprint_testing_total, dev_bug_fixing, project_management_total,
        total_billable_hours, dev_budget, license, total_budget,
      }
    },

    applyToForm() {
      if (!this.breakdown) return
      this.$emit('apply', this.breakdown.base_effort_estimation)
      this.$emit('close')
    },

    onBudgetInput(event) {
      const cleaned = parseFloat(event.target.value.replace(/,/g, ''))
      this.targetBudget = isNaN(cleaned) ? null : cleaned
    }
  },
}
</script>

<style scoped>
.budget-drawer-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 1050;
  display: flex;
  justify-content: flex-end;
}

.budget-drawer {
  width: 460px;
  max-width: 100%;
  background: #fff;
  display: flex;
  flex-direction: column;
  height: 100%;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.15);
}

.budget-drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #dee2e6;
  flex-shrink: 0;
}

.budget-drawer-body {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
}

.budget-drawer-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #dee2e6;
  flex-shrink: 0;
}

/* Slide-in transition */
.drawer-enter-active,
.drawer-leave-active {
  transition: opacity 0.25s ease;
}
.drawer-enter-active .budget-drawer,
.drawer-leave-active .budget-drawer {
  transition: transform 0.25s ease;
}
.drawer-enter .budget-drawer-overlay,
.drawer-leave-to .budget-drawer-overlay {
  opacity: 0;
}
.drawer-enter .budget-drawer,
.drawer-leave-to .budget-drawer {
  transform: translateX(100%);
}

.calc-info {
  color: #6c757d;
  font-size: 0.75rem;
  margin-left: 3px;
  vertical-align: middle;
  opacity: 0.6;
}
.calc-info:hover {
  opacity: 1;
}
</style>
